/**
 * Demo simplification — a SINGLE reversible switch that collapses the demo's
 * many personas down to the two the presentation actually needs:
 *
 *   - exactly ONE subscriber persona (Premium), and
 *   - exactly ONE staff persona ('Admin', all-access)
 *
 *   NEXT_PUBLIC_DEMO_SIMPLE_ROLES=true   → simplified persona surface
 *   (unset, or =false)                   → the full demo cast (free/pro/premium
 *                                          subscribers + Hersh/Kieran/campaign)
 *
 * This ONLY affects what the *presentation* surfaces show — the login quick-
 * pick buttons (components/demo-quick-login.tsx) and the demo banner's
 * "switch user" chips (components/demo-banner.tsx). It does NOT touch RBAC: the
 * full allowlists in lib/auth/tier-gate.ts, lib/supabase/demo-client.ts and
 * app/api/v1/demo/login/route.ts stay intact so every role can still be
 * exercised by URL / RBAC tests. Nothing is deleted; flip the env var off to
 * bring the whole cast back.
 *
 * Mirrors the shape of lib/launch-mode.ts (a NEXT_PUBLIC_ flag read identically
 * by server and client, inlined at build time — a change needs a dev-server
 * restart / redeploy to take effect).
 */

export const DEMO_SIMPLE = process.env.NEXT_PUBLIC_DEMO_SIMPLE_ROLES === 'true';

export interface DemoSubscriberPersona {
  email: string;
  /** Short label for the login quick-pick button. */
  label: string;
  /** Longer label for the banner chip. */
  bannerLabel: string;
  tier: 'free' | 'pro' | 'premium';
}

export interface DemoStaffPersona {
  email: string;
  /** Banner chip label. */
  bannerLabel: string;
  /** Role the persona maps to (informational; RBAC source of truth is tier-gate.ts). */
  role: string;
}

/** Every subscriber persona the demo can present, full → simple in that order. */
const ALL_SUBSCRIBER_PERSONAS: readonly DemoSubscriberPersona[] = [
  {
    email: 'demo+premium@stocksinglobal.com.au',
    label: 'Premium',
    bannerLabel: 'Premium subscriber',
    tier: 'premium',
  },
  {
    email: 'demo+pro@stocksinglobal.com.au',
    label: 'Pro',
    bannerLabel: 'Pro subscriber',
    tier: 'pro',
  },
  {
    email: 'demo+basic@stocksinglobal.com.au',
    label: 'Basic',
    bannerLabel: 'Free subscriber',
    tier: 'free',
  },
] as const;

/**
 * Every staff persona the demo can present. In simple mode only the single
 * all-access 'Admin' chip is surfaced; the scoped staff personas (Hersh/CIO,
 * Kieran/senior analyst, campaign_crm) stay in the RBAC allowlists but are not
 * shown.
 */
const ALL_STAFF_PERSONAS: readonly DemoStaffPersona[] = [
  {
    email: 'admin@stocksinglobal.com.au',
    bannerLabel: 'Admin (all access)',
    role: 'admin',
  },
  {
    email: 'hersh@stocksinglobal.com.au',
    bannerLabel: 'Hersh (CIO)',
    role: 'cio',
  },
  {
    email: 'kieran@stocksinglobal.com.au',
    bannerLabel: 'Kieran (Senior analyst)',
    role: 'senior_analyst',
  },
  {
    email: 'campaign+demo@stocksinglobal.com.au',
    bannerLabel: 'Campaign + CRM',
    role: 'campaign_crm',
  },
] as const;

/**
 * Subscriber personas the presentation surfaces should show. In simple mode:
 * only Premium. Otherwise the full free/pro/premium cast.
 */
export function visibleSubscriberPersonas(): DemoSubscriberPersona[] {
  if (DEMO_SIMPLE) {
    return ALL_SUBSCRIBER_PERSONAS.filter((p) => p.tier === 'premium');
  }
  return [...ALL_SUBSCRIBER_PERSONAS];
}

/**
 * Staff personas the presentation surfaces should show. In simple mode: only
 * the single all-access 'Admin'. Otherwise the full staff cast.
 */
export function visibleStaffPersonas(): DemoStaffPersona[] {
  if (DEMO_SIMPLE) {
    return ALL_STAFF_PERSONAS.filter((p) => p.role === 'admin');
  }
  // Full mode keeps the historic banner cast (Hersh + Kieran). The campaign_crm
  // and admin personas stay in the RBAC allowlists but were never banner chips,
  // so they are not surfaced here either.
  return ALL_STAFF_PERSONAS.filter((p) => p.role === 'cio' || p.role === 'senior_analyst');
}
